import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getContracts,
  getContractViewLink,
  getContract,
  cancelContract,
  deleteContract,
  createContract,
  resendContract,
  getTemplates,
  getClients,
} from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Chip,
  Alert,
  Pagination,
  ListItemText,
} from "@/components/ui";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import LinkIcon from "@mui/icons-material/Link";
import AppLoading from "@/components/AppLoading";
import { DownloadIcon, PlusCircleIcon } from "@/components/icons";

const escapeCsv = (v) => {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const PERIOD_OPTIONS = [7, 30, 90, 0]; // 0 = All

const EXPIRY_OPTIONS = [
  { value: 24, labelHe: "24 שעות", labelEn: "24 hours" },
  { value: 48, labelHe: "48 שעות", labelEn: "48 hours" },
  { value: 72, labelHe: "72 שעות", labelEn: "72 hours" },
];

const STATUS_OPTIONS = [
  { value: "", labelHe: "כל הסטטוסים", labelEn: "All Statuses" },
  { value: "PENDING", labelHe: "ממתין", labelEn: "Pending" },
  { value: "VIEWED", labelHe: "נצפה", labelEn: "Viewed" },
  { value: "SIGNED", labelHe: "נחתם", labelEn: "Signed" },
  { value: "EXPIRED", labelHe: "פג תוקף", labelEn: "Expired" },
  { value: "CANCELLED", labelHe: "בוטל", labelEn: "Cancelled" },
];

/** מקסימום שליחות SMS לחוזה */
const MAX_RESEND_COUNT = 5;

const CompanyContracts = () => {
  const { t, isRTL, language } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [period, setPeriod] = useState(0);
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [copySuccessId, setCopySuccessId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    templateId: "",
    clientIds: [],
    contractName: "",
    name: "",
    phone: "",
    email: "",
    expiresInHours: 72,
    sendSms: true,
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const contractId = (c) => c.contractId || c._id;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contracts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(contracts.map((c) => contractId(c))));
  };

  const selectedContracts = contracts.filter((c) =>
    selectedIds.has(contractId(c)),
  );
  const canBulkCancel = selectedContracts.some(
    (c) => c.status === "PENDING" || c.status === "VIEWED",
  );
  const canBulkDelete = selectedContracts.some((c) => c.status === "CANCELLED");
  const canBulkDownload = selectedContracts.some((c) => c.status === "SIGNED");

  const handleBulkCancel = async () => {
    const toCancelIds = selectedContracts
      .filter((c) => c.status === "PENDING" || c.status === "VIEWED")
      .map((c) => contractId(c));
    if (toCancelIds.length === 0) return;
    if (
      !window.confirm(
        isRTL
          ? `לבטל ${toCancelIds.length} חוזים?`
          : `Cancel ${toCancelIds.length} contracts?`,
      )
    )
      return;
    setBulkLoading(true);
    try {
      await Promise.all(toCancelIds.map((id) => cancelContract(id)));
      setSelectedIds(new Set());
      await loadContracts();
    } catch (err) {
      console.error("Bulk cancel failed:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const toDeleteIds = selectedContracts
      .filter((c) => c.status === "CANCELLED")
      .map((c) => contractId(c));
    if (toDeleteIds.length === 0) return;
    if (
      !window.confirm(
        isRTL
          ? `למחוק ${toDeleteIds.length} חוזים לצמיתות?`
          : `Permanently delete ${toDeleteIds.length} contracts?`,
      )
    )
      return;
    setBulkLoading(true);
    try {
      await Promise.all(toDeleteIds.map((id) => deleteContract(id)));
      setSelectedIds(new Set());
      await loadContracts();
    } catch (err) {
      console.error("Bulk delete failed:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDownloadPdf = async () => {
    const signed = selectedContracts.filter((c) => c.status === "SIGNED");
    if (signed.length === 0) return;
    setBulkLoading(true);
    const windows = signed.map(() => window.open("", "_blank"));
    try {
      for (let i = 0; i < signed.length; i++) {
        const data = await getContract(contractId(signed[i]));
        if (windows[i] && data?.pdfUrl) windows[i].location.href = data.pdfUrl;
        else if (windows[i]) windows[i].close();
        if (i < signed.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
    } catch (err) {
      console.error("Bulk download failed:", err);
      windows.forEach((w) => w?.close());
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, [page, statusFilter, search, period, expiringSoon]);

  useEffect(() => {
    if (showCreateModal) {
      getTemplates().then((d) => setTemplates(d?.templates || []));
      getClients({ limit: 200 }).then((d) => setClients(d?.clients || []));
    }
  }, [showCreateModal]);

  const getDateFilter = () => {
    if (expiringSoon) return { expiringWithinHours: 24 };
    if (period === 0) return {};
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - period);
    return { fromDate: fromDate.toISOString() };
  };

  const hasActiveFilters =
    search || statusFilter || period !== 0 || expiringSoon;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPeriod(0);
    setExpiringSoon(false);
    setPage(1);
    setSearchParams({});
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await getContracts({
        page,
        limit: 15,
        status: statusFilter || undefined,
        search: search || undefined,
        ...getDateFilter(),
      });
      setContracts(data?.contracts || []);
      setSelectedIds(new Set());
      setTotalCount(data?.pagination?.total ?? data?.total ?? 0);
      setTotalPages(
        data?.pagination?.pages ||
          data?.totalPages ||
          Math.ceil((data?.total || 0) / 15) ||
          1,
      );
    } catch (error) {
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTimeLeft = (expiresAt, status) => {
    if (!expiresAt || (status !== "PENDING" && status !== "VIEWED")) return "-";
    const now = new Date();
    const exp = new Date(expiresAt);
    if (exp <= now) return isRTL ? "פג" : "Expired";
    const ms = exp - now;
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const days = Math.floor(hours / 24);
    if (days > 0)
      return isRTL ? `${days} ימים` : `${days}d`;
    if (hours > 0)
      return isRTL ? `${hours} שעות` : `${hours}h`;
    const mins = Math.floor(ms / (60 * 1000));
    return isRTL ? `${mins} דק׳` : `${mins}m`;
  };

  const exportToCsv = () => {
    const headers = isRTL
      ? [
          "שם חוזה",
          "שם תבנית",
          "לקוח",
          "טלפון",
          "אימייל",
          "סטטוס",
          "תאריך יצירה",
          "תאריך תפוגה",
          "זמן נותר",
          "שליחות",
          "תאריך חתימה",
          "מזהה חיצוני",
        ]
      : [
          "Contract name",
          "Template name",
          "Client",
          "Phone",
          "Email",
          "Status",
          "Created",
          "Expires at",
          "Time left",
          "Resends",
          "Signed at",
          "External ref",
        ];
    const rows = contracts.map((c) => [
      escapeCsv(c.name ?? ""),
      escapeCsv(c.templateName ?? ""),
      escapeCsv(c.client?.name ?? c.clientName ?? ""),
      escapeCsv(c.client?.phone ?? c.clientPhone ?? ""),
      escapeCsv(c.client?.email ?? c.clientEmail ?? ""),
      escapeCsv(getStatusLabel(c.status)),
      escapeCsv(formatDate(c.createdAt)),
      escapeCsv(formatDate(c.expiresAt)),
      escapeCsv(formatTimeLeft(c.expiresAt, c.status)),
      escapeCsv(`${c.resendCount ?? 0}/${MAX_RESEND_COUNT}`),
      escapeCsv(formatDate(c.signature?.signedAt)),
      escapeCsv(c.externalRef ?? ""),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contracts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusLabel = (status) => {
    const labels = {
      he: {
        PENDING: "ממתין",
        VIEWED: "נצפה",
        SIGNED: "נחתם",
        EXPIRED: "פג תוקף",
        CANCELLED: "בוטל",
      },
      en: {
        PENDING: "Pending",
        VIEWED: "Viewed",
        SIGNED: "Signed",
        EXPIRED: "Expired",
        CANCELLED: "Cancelled",
      },
    };
    return labels[language]?.[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "SIGNED":
        return "success";
      case "PENDING":
      case "VIEWED":
        return "warning";
      case "EXPIRED":
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const handleView = async (c) => {
    const id = contractId(c);
    setActionLoading(id);
    try {
      const viewLink = await getContractViewLink(id);
      window.open(viewLink, "_blank");
    } catch (err) {
      console.error("Failed to get view link:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyLink = async (c) => {
    const id = contractId(c);
    setActionLoading(id);
    setCopySuccessId(null);
    try {
      const viewLink = await getContractViewLink(id);
      await navigator.clipboard.writeText(viewLink);
      setCopySuccessId(id);
      setTimeout(() => setCopySuccessId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResend = async (c) => {
    const id = contractId(c);
    if (
      !window.confirm(
        isRTL
          ? "לשלוח שוב קישור חתימה ב-SMS ללקוח?"
          : "Resend signature link via SMS to client?",
      )
    )
      return;
    setActionLoading(id);
    try {
      const data = await resendContract(id);
      // עדכון מיידי של מספר השליחות במצב (התגובה מהשרת מכילה את resendCount המעודכן)
      if (data?.resendCount != null) {
        setContracts((prev) =>
          prev.map((contract) =>
            contractId(contract) === id
              ? { ...contract, resendCount: data.resendCount }
              : contract
          )
        );
      } else {
        await loadContracts();
      }
    } catch (err) {
      console.error("Failed to resend:", err);
      alert(err.response?.data?.message || (isRTL ? "שליחה נכשלה" : "Resend failed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (c) => {
    const id = contractId(c);
    setActionLoading(id);
    const win = window.open("", "_blank");
    try {
      const data = await getContract(id);
      if (data?.pdfUrl && win) win.location.href = data.pdfUrl;
      else if (win) win.close();
    } catch (err) {
      console.error("Failed to get PDF:", err);
      if (win) win.close();
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (c) => {
    const id = contractId(c);
    if (!window.confirm(isRTL ? "לבטל את החוזה?" : "Cancel this contract?"))
      return;
    setActionLoading(id);
    try {
      await cancelContract(id);
      await loadContracts();
    } catch (err) {
      console.error("Failed to cancel:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (c) => {
    const id = contractId(c);
    if (
      !window.confirm(
        isRTL ? "למחוק את החוזה לצמיתות?" : "Permanently delete this contract?",
      )
    )
      return;
    setActionLoading(id);
    try {
      await deleteContract(id);
      await loadContracts();
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const initialCreateForm = {
    templateId: "",
    clientIds: [],
    name: "",
    phone: "",
    email: "",
    expiresInHours: 72,
    sendSms: true,
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm(initialCreateForm);
  };

  const getRecipientsFromForm = () => {
    const list = [];
    const ids = Array.isArray(createForm.clientIds) ? createForm.clientIds : [];
    for (const id of ids) {
      if (id === "new") {
        const name = createForm.name?.trim();
        const phone = (createForm.phone || "").replace(/[\s-]/g, "");
        if (name && phone)
          list.push({ name, phone, email: (createForm.email || "").trim() });
      } else {
        const c = clients.find((x) => x._id === id);
        if (c)
          list.push({ name: c.name, phone: c.phone, email: c.email || "" });
      }
    }
    return list;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const nameTrimmed = createForm.contractName?.trim();
    if (!nameTrimmed) return;
    const recipients = getRecipientsFromForm();
    if (recipients.length === 0) return;
    setCreateLoading(true);
    try {
      const payload = {
        templateId: createForm.templateId || undefined,
        expiresInHours: createForm.expiresInHours,
        sendSms: createForm.sendSms,
        contract: { name: nameTrimmed },
      };
      for (const client of recipients) {
        await createContract({ ...payload, client });
      }
      closeCreateModal();
      await loadContracts();
    } catch (err) {
      console.error("Failed to create contract:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      {/* Row 1: Page title + Create & Export buttons */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t("contracts")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isRTL
              ? `סה״כ ${totalCount} חוזים`
              : `Total: ${totalCount} contracts`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={exportToCsv}
            disabled={contracts.length === 0}
            sx={{ color: "#fff" }}
            endIcon={
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  "& svg": { width: 18, height: 18 },
                }}
              >
                <DownloadIcon />
              </Box>
            }
          >
            {isRTL ? "ייצוא ל-CSV" : "Export CSV"}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setShowCreateModal(true)}
            endIcon={
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  "& svg": { width: 20, height: 20 },
                }}
              >
                <PlusCircleIcon />
              </Box>
            }
          >
            {isRTL ? "צור חוזה" : "Create Contract"}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={showCreateModal}
        onClose={closeCreateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isRTL ? "צור חוזה ושליחה ב-SMS" : "Create contract & send SMS"}
        </DialogTitle>
        <form onSubmit={handleCreateSubmit}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0 }}>
              <TextField
                label={isRTL ? "שם חוזה" : "Contract name"}
                required
                value={createForm.contractName}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, contractName: e.target.value }))
                }
                fullWidth
                size="small"
                placeholder={
                  isRTL
                    ? "למשל: חוזה שירות 2026"
                    : "e.g. Service agreement 2026"
                }
              />
              <FormControl fullWidth size="small">
                <InputLabel>{isRTL ? "תבנית" : "Template"}</InputLabel>
                <Select
                  value={createForm.templateId}
                  label={isRTL ? "תבנית" : "Template"}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, templateId: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    {isRTL ? "ברירת מחדל" : "Default"}
                  </MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.name} {t.version ? `(v${t.version})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {isRTL
                    ? "לקוחות (ניתן לבחור כמה)"
                    : "Clients (select multiple)"}
                </InputLabel>
                <Select
                  multiple
                  value={createForm.clientIds}
                  label={
                    isRTL
                      ? "לקוחות (ניתן לבחור כמה)"
                      : "Clients (select multiple)"
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    const arr = typeof v === "string" ? (v ? [v] : []) : v || [];
                    if (arr.includes("__select_all__")) {
                      const allIds = clients.map((c) => c._id);
                      const allSelected = allIds.length > 0 && allIds.every((id) => createForm.clientIds.includes(id));
                      setCreateForm((f) => ({ ...f, clientIds: allSelected ? [] : allIds }));
                      return;
                    }
                    setCreateForm((f) => ({ ...f, clientIds: arr }));
                  }}
                  renderValue={(selected) => {
                    if (!selected?.length) return "";
                    const parts = [];
                    if (selected.includes("new"))
                      parts.push(isRTL ? "פרטים חדשים" : "New client");
                    selected
                      .filter((id) => id !== "new")
                      .forEach((id) => {
                        const c = clients.find((x) => x._id === id);
                        if (c) parts.push(`${c.name} – ${c.phone}`);
                      });
                    return parts.join(", ");
                  }}
                >
                  <MenuItem value="__select_all__" sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ListItemText primary={isRTL ? "בחר הכל" : "Select all"} />
                    <Checkbox
                      checked={clients.length > 0 && clients.every((c) => createForm.clientIds.includes(c._id))}
                      size="small"
                      sx={{ p: 0, ml: "auto" }}
                    />
                  </MenuItem>
                  {clients.map((c) => (
                    <MenuItem
                      key={c._id}
                      value={c._id}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Checkbox
                        checked={createForm.clientIds.includes(c._id)}
                        size="small"
                        sx={{ mr: 0, p: 0 }}
                      />
                      <ListItemText primary={`${c.name} – ${c.phone}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {createForm.clientIds.includes("new") && (
                <>
                  <TextField
                    label={isRTL ? "שם לקוח" : "Client name"}
                    required
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, name: e.target.value }))
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={isRTL ? "טלפון" : "Phone"}
                    required
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="0500000000"
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr", type: "tel", inputMode: "tel" }}
                  />
                  <TextField
                    label={isRTL ? "אימייל" : "Email"}
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, email: e.target.value }))
                    }
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr" }}
                  />
                </>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>{isRTL ? "תפוגה" : "Expiration"}</InputLabel>
                <Select
                  value={createForm.expiresInHours}
                  label={isRTL ? "תפוגה" : "Expiration"}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      expiresInHours: Number(e.target.value),
                    }))
                  }
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {language === "he" ? o.labelHe : o.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createForm.sendSms}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        sendSms: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  isRTL ? "שלח לינק חתימה ב-SMS" : "Send signature link via SMS"
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                setShowCreateModal(false);
                setCreateForm({
                  templateId: "",
                  clientIds: [],
                  contractName: "",
                  name: "",
                  phone: "",
                  email: "",
                  expiresInHours: 72,
                  sendSms: true,
                });
              }}
            >
              {isRTL ? "ביטול" : "Cancel"}
            </Button>
            <Button
              size="small"
              type="submit"
              variant="contained"
              disabled={
                createLoading ||
                !createForm.contractName?.trim() ||
                getRecipientsFromForm().length === 0
              }
            >
              {createLoading
                ? isRTL
                  ? "יוצר..."
                  : "Creating..."
                : isRTL
                  ? "צור ושלוח"
                  : "Create & Send"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Row 2: Filters & search – just above the table */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          {isRTL
            ? "כדי למחוק חוזה, יש לבטל אותו קודם. כפתור המחיקה יופיע רק עבור חוזים שבוטלו."
            : "To delete a contract, you must cancel it first. The delete button will only appear for cancelled contracts."}
        </Typography>
        <TextField
          size="small"
          placeholder={
            isRTL ? "חיפוש לפי שם או טלפון..." : "Search by name or phone..."
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{isRTL ? "סטטוס" : "Status"}</InputLabel>
          <Select
            value={statusFilter}
            label={isRTL ? "סטטוס" : "Status"}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value || "all"} value={o.value}>
                {language === "he" ? o.labelHe : o.labelEn}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, v) => {
            if (v != null) {
              setExpiringSoon(false);
              setPeriod(v);
            }
          }}
          size="small"
          aria-label="period"
          sx={{
            "& .MuiToggleButton-root": { px: 1.75 },
            ...(isRTL && {
              flexDirection: "row-reverse",
              "& .MuiToggleButtonGroup-grouped": { flexDirection: "row-reverse" },
            }),
          }}
        >
          {(isRTL ? [...PERIOD_OPTIONS].reverse() : PERIOD_OPTIONS).map((d) => (
            <ToggleButton
              key={d}
              value={d}
              aria-label={d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
              sx={isRTL ? { direction: "rtl" } : undefined}
            >
              {d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Chip
          label={isRTL ? "פוג ב-24 שעות" : "Expiring 24h"}
          onClick={() => {
            setExpiringSoon((prev) => !prev);
            if (!expiringSoon) setPeriod(0);
          }}
          color={expiringSoon ? "primary" : "default"}
          variant={expiringSoon ? "filled" : "outlined"}
          size="small"
          sx={{
            cursor: "pointer",
            height: 40,
            minHeight: 40,
            boxSizing: "border-box",
            "& .MuiChip-label": { px: 1.5, lineHeight: 1.25 },
          }}
        />
        {hasActiveFilters && (
          <Button
            size="small"
            variant="outlined"
            onClick={clearFilters}
            sx={{ minWidth: "auto" }}
          >
            {isRTL ? "נקה סינון" : "Clear filter"}
          </Button>
        )}
      </Stack>

      {loading ? (
        <AppLoading fullPage />
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "רשימת חוזים" : "Contracts List"}
            </Typography>

            {selectedIds.size > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" fontWeight={500}>
                  {isRTL
                    ? `${selectedIds.size} חוזים נבחרו`
                    : `${selectedIds.size} selected`}
                </Typography>
                {canBulkCancel && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={handleBulkCancel}
                    disabled={bulkLoading}
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                      borderColor: "rgba(239,68,68,0.5)",
                      color: "error.main",
                      "&:hover": {
                        borderColor: "error.main",
                        bgcolor: "rgba(239,68,68,0.08)",
                      },
                    }}
                  >
                    {bulkLoading
                      ? "..."
                      : isRTL
                        ? `ביטול (${
                            selectedContracts.filter(
                              (c) =>
                                c.status === "PENDING" || c.status === "VIEWED",
                            ).length
                          })`
                        : `Cancel (${
                            selectedContracts.filter(
                              (c) =>
                                c.status === "PENDING" || c.status === "VIEWED",
                            ).length
                          })`}
                  </Button>
                )}
                {canBulkDownload && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleBulkDownloadPdf}
                    disabled={bulkLoading}
                    endIcon={
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          "& svg": { width: 16, height: 16 },
                        }}
                      >
                        <DownloadIcon />
                      </Box>
                    }
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                      bgcolor: "#fff",
                      color: "#000",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        bgcolor: "grey.200",
                        borderColor: "grey.300",
                      },
                    }}
                  >
                    {bulkLoading
                      ? "..."
                      : isRTL
                        ? `הורד את כולם (${selectedContracts.filter((c) => c.status === "SIGNED").length})`
                        : `Download all (${selectedContracts.filter((c) => c.status === "SIGNED").length})`}
                  </Button>
                )}
                {canBulkDelete && (
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={handleBulkDelete}
                    disabled={bulkLoading}
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                    }}
                  >
                    {bulkLoading
                      ? "..."
                      : isRTL
                        ? `מחיקה (${
                            selectedContracts.filter(
                              (c) => c.status === "CANCELLED",
                            ).length
                          })`
                        : `Delete (${
                            selectedContracts.filter(
                              (c) => c.status === "CANCELLED",
                            ).length
                          })`}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkLoading}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.25,
                    fontSize: "0.75rem",
                  }}
                >
                  {isRTL ? "בטל בחירה" : "Clear"}
                </Button>
              </Stack>
            )}

            {contracts.length === 0 ? (
              <Box py={6} textAlign="center">
                <Typography color="text.secondary">
                  {isRTL ? "אין חוזים" : "No contracts"}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              contracts.length > 0 &&
                              selectedIds.size === contracts.length
                            }
                            indeterminate={
                              selectedIds.size > 0 &&
                              selectedIds.size < contracts.length
                            }
                            onChange={toggleSelectAll}
                          />
                        </TableCell>
                        <TableCell>
                          {isRTL ? "שם חוזה" : "Contract name"}
                        </TableCell>
                        <TableCell>
                          {isRTL ? "שם תבנית" : "Template name"}
                        </TableCell>
                        <TableCell>{isRTL ? "לקוח" : "Client"}</TableCell>
                        <TableCell>{isRTL ? "טלפון" : "Phone"}</TableCell>
                        <TableCell>{isRTL ? "סטטוס" : "Status"}</TableCell>
                        <TableCell>{isRTL ? "זמן נותר" : "Time left"}</TableCell>
                        <TableCell>{isRTL ? "שליחות" : "Resends"}</TableCell>
                        <TableCell>{isRTL ? "נוצר" : "Created"}</TableCell>
                        <TableCell>{isRTL ? "פעולות" : "Actions"}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contracts.map((contract) => (
                        <TableRow
                          key={contractId(contract)}
                          selected={selectedIds.has(contractId(contract))}
                          hover
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(contractId(contract))}
                              onChange={() =>
                                toggleSelect(contractId(contract))
                              }
                            />
                          </TableCell>
                          <TableCell>{contract.name || "-"}</TableCell>
                          <TableCell>{contract.templateName || "-"}</TableCell>
                          <TableCell>{contract.client?.name || "-"}</TableCell>
                          <TableCell>
                            <span dir="ltr">
                              {contract.client?.phone || "-"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(contract.status)}
                              size="small"
                              color={getStatusColor(contract.status)}
                              sx={{ color: "#fff" }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatTimeLeft(contract.expiresAt, contract.status)}
                          </TableCell>
                          <TableCell>
                            {(contract.resendCount ?? 0)}/{MAX_RESEND_COUNT}
                          </TableCell>
                          <TableCell>
                            {formatDate(contract.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleView(contract)}
                                disabled={
                                  actionLoading === contractId(contract)
                                }
                                title={isRTL ? "צפה" : "View"}
                                aria-label={isRTL ? "צפה" : "View"}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {(contract.status === "PENDING" ||
                                contract.status === "VIEWED") && (
                                <>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCopyLink(contract)}
                                    disabled={
                                      actionLoading === contractId(contract)
                                    }
                                    title={
                                      copySuccessId === contractId(contract)
                                        ? isRTL
                                          ? "הועתק!"
                                          : "Copied!"
                                        : isRTL
                                          ? "העתק קישור"
                                          : "Copy link"
                                    }
                                    aria-label={
                                      isRTL ? "העתק קישור" : "Copy link"
                                    }
                                  >
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleResend(contract)}
                                    disabled={
                                      actionLoading === contractId(contract) ||
                                      (contract.resendCount ?? 0) >= MAX_RESEND_COUNT
                                    }
                                    title={isRTL ? "שלח שוב" : "Resend"}
                                    aria-label={isRTL ? "שלח שוב" : "Resend"}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {contract.status === "SIGNED" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleDownloadPdf(contract)}
                                  disabled={
                                    actionLoading === contractId(contract)
                                  }
                                  endIcon={
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-flex",
                                        "& svg": { width: 16, height: 16 },
                                      }}
                                    >
                                      <DownloadIcon />
                                    </Box>
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.25,
                                    fontSize: "0.75rem",
                                    bgcolor: "#fff",
                                    color: "#000",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    "&:hover": {
                                      bgcolor: "grey.200",
                                      borderColor: "grey.300",
                                    },
                                  }}
                                >
                                  {isRTL ? "הורד PDF" : "Download PDF"}
                                </Button>
                              )}
                              {(contract.status === "PENDING" ||
                                contract.status === "VIEWED") && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => handleCancel(contract)}
                                  disabled={
                                    actionLoading === contractId(contract)
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.25,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {isRTL ? "ביטול" : "Cancel"}
                                </Button>
                              )}
                              {contract.status === "CANCELLED" && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  onClick={() => handleDelete(contract)}
                                  disabled={
                                    actionLoading === contractId(contract)
                                  }
                                  sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.25,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {isRTL ? "מחק" : "Delete"}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, p) => setPage(p)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CompanyContracts;
